import { useState, useEffect, useCallback } from 'react';
import { db, type ColorHistoryRecord } from '../lib/database';

export interface UseColorHistoryResult {
  history: ColorHistoryRecord[];
  loading: boolean;
  error: string | null;
  addToHistory: (hex: string, format: string, displayValue: string, source?: ColorHistoryRecord['source']) => Promise<void>;
  clearHistory: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useColorHistory(limit: number = 50): UseColorHistoryResult {
  const [history, setHistory] = useState<ColorHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await db.getHistory(limit);
      setHistory(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
      console.error('Error loading color history:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const addToHistory = useCallback(async (
    hex: string,
    format: string,
    displayValue: string,
    source: ColorHistoryRecord['source'] = 'manual'
  ) => {
    try {
      await db.addToHistory({
        hex: hex.toLowerCase(),
        format,
        displayValue,
        source,
      });
      await loadHistory();
    } catch (err) {
      console.error('Error adding to history:', err);
      throw err;
    }
  }, [loadHistory]);

  const clearHistory = useCallback(async () => {
    try {
      await db.clearHistory();
      setHistory([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear history');
      throw err;
    }
  }, []);

  return {
    history,
    loading,
    error,
    addToHistory,
    clearHistory,
    refresh: loadHistory,
  };
}
